// src/pages/About.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem 4rem 2rem; /* Increased top padding from 4rem to 6rem */
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  color: ${props => props.theme?.colors?.primary || '#000'};
  margin-bottom: 4rem;
  text-align: center;
`;

const ContentSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: ${props => props.theme?.breakpoints?.tablet || '768px'}) {
    grid-template-columns: 1fr;
  }
`;

const TextContent = styled(motion.div)`
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageContainer = styled(motion.div)`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const TeamSection = styled.section`
  margin-top: 4rem;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const TeamMember = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: 300px;
    object-fit: cover;
  }
  
  .member-info {
    padding: 1.5rem;
    
    h3 {
      color: ${props => props.theme?.colors?.primary || '#000'};
      margin-bottom: 0.5rem;
    }
    
    p {
      color: ${props => props.theme?.colors?.lightText || '#666'};
      font-size: 0.9rem;
    }
  }
`;

const About = () => {
  return (
    <AboutContainer>
      <PageTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        About L&J Agape Healthcare Ltd
      </PageTitle>
      
      <ContentSection>
        <TextContent
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2>Our Mission</h2>
          <p>
            At L&J Agape Healthcare Ltd, our mission is to provide compassionate, personalized care that enhances the quality of life for our clients while allowing them to maintain their independence and dignity in the comfort of their own homes.
          </p>
          <p>
            Founded on the principles of empathy and professional excellence, we strive to be the leading provider of domiciliary care services, setting the standard for quality, reliability, and client satisfaction in healthcare support.
          </p>
          <p>
            Our team consists of highly trained professionals who are not just caregivers but companions, dedicated to creating meaningful relationships with each client and their families.
          </p>
        </TextContent>
        
        <ImageContainer
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <img src="/images/about-image.jpg" alt="Caregivers with client" />
        </ImageContainer>
      </ContentSection>
      
      <TeamSection>
        <h2>Meet Our Manager</h2>
        <TeamGrid>
          <TeamMember
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <img src="/images/team-1.jpg" alt="Abigail Oppong" />
            <div className="member-info">
              <h3>Abigail Oppong</h3>
              <p>Registered Manager</p>
              <p>As an experienced healthcare management professional, Abigail is dedicated to upholding the highest standards of care and compliance across our services.</p>
            </div>
          </TeamMember>
          
          {/* Add more team members as needed */}
        </TeamGrid>
      </TeamSection>
    </AboutContainer>
  );
};

export default About;